import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Info, AlertTriangle, ChevronRight } from 'lucide-react';
import { UncontrolledTooltip } from 'reactstrap';
import ReportWidget from './components/ReportWidget';
import AssetSpendCard from './AssetSpendCard';
import { fetchMaintenanceSpendByAsset } from '../../store/reporting/actions';
import { formatDateTimeForAPI } from '../../constants/utils';
import { formatCurrency as formatCurrencyBase } from '../../helpers/formatCurrency';

const EM_DASH = '—';
const DEFAULT_LIMIT = 10;

const formatMoney = (value, currency) =>
  formatCurrencyBase(value, currency, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

/**
 * Maintenance Spend by Asset — Top-N widget for the Reporting → Asset Health tab.
 *
 * Header KPI strip (Total spend / Avg. maintenance spend / Corrective ratio) + a list of rich
 * {@link AssetSpendCard}s, sorted by spend descending server-side. "See all" deep-links to the
 * expanded list view at /reporting/asset-spend.
 */
const MaintenanceSpendByAssetWidget = ({
  organizationId, dateRange, criticality, locationId, vendorId, assetId, assetTypeId, contractNumber,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    maintenanceSpendByAsset: data,
    maintenanceSpendByAssetLoading: loading,
    maintenanceSpendByAssetError: error,
  } = useSelector((state) => state.Reporting);

  const fetchData = useCallback(() => {
    if (!organizationId || !dateRange?.startDate || !dateRange?.endDate) return;
    dispatch(fetchMaintenanceSpendByAsset(
      organizationId,
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      criticality, locationId, vendorId, assetId, assetTypeId, contractNumber,
      DEFAULT_LIMIT, 0, 'totalSpend',
    ));
  }, [dispatch, organizationId, dateRange, criticality, locationId, vendorId, assetId, assetTypeId, contractNumber]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const summary = data?.summary;
  const items = useMemo(() => data?.items || [], [data?.items]);
  const pagination = data?.pagination;
  const currency = summary?.currency;

  const maxSpend = useMemo(() => {
    if (!items.length) return 0;
    return items.reduce(
      (max, item) => (item.totalSpend != null && Number(item.totalSpend) > max
        ? Number(item.totalSpend) : max),
      0,
    );
  }, [items]);

  const correctiveRatioPct = summary?.correctiveRatio != null
    ? Math.round(summary.correctiveRatio * 100)
    : null;
  const isEmpty = !data || items.length === 0;

  return (
    <ReportWidget
      title={
        <span className="d-flex align-items-center gap-2">
          {t('reporting.maintenance_spend_by_asset.title', 'Maintenance Spend by Asset')}
          <Info
            size={15}
            className="text-muted"
            id="maintenanceSpendByAssetTooltip"
            style={{ cursor: 'help' }}
          />
          <UncontrolledTooltip placement="top" target="maintenanceSpendByAssetTooltip">
            {t('reporting.maintenance_spend_by_asset.tooltip', 'Labor + parts + contractor cost per asset, summed across work orders in the period. Downtime cost is shown separately in the Downtime Cost report.')}
          </UncontrolledTooltip>
        </span>
      }
      loading={loading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage={t('reporting.maintenance_spend_by_asset.empty', 'No maintenance spend recorded for the selected period.')}
      onRefresh={fetchData}
    >
      {/* Summary KPI strip */}
      {summary && (
        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <div className="p-3 rounded bg-light">
              <div className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                {t('reporting.cost.summary.total_spend', 'Total spend')}
              </div>
              <div className="fw-bold" style={{ fontSize: '1.4rem' }}>
                {formatMoney(summary.totalSpend, currency)}
              </div>
              <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                {t('reporting.maintenance_spend_by_asset.across_assets', '{{count}} asset', { count: summary.assetsInScope })}
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="p-3 rounded bg-light">
              <div className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                {t('reporting.maintenance_spend_by_asset.avg_top_n', 'Avg. maintenance spend')}
              </div>
              <div className="fw-bold" style={{ fontSize: '1.4rem' }}>
                {formatMoney(summary.topNAvgSpend, currency)}
              </div>
              <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                {t('reporting.maintenance_spend_by_asset.top_n_assets', 'top {{count}} assets', { count: summary.topNCount })}
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="p-3 rounded bg-light">
              <div className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                {t('reporting.maintenance_spend_by_asset.corrective_ratio', 'Corrective ratio')}
              </div>
              <div className="fw-bold" style={{ fontSize: '1.4rem' }}>
                {correctiveRatioPct != null ? `${correctiveRatioPct}%` : EM_DASH}
              </div>
              <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                {t('reporting.maintenance_spend_by_asset.corrective_vs_preventive', 'corrective vs preventive')}
              </div>
            </div>
          </div>
        </div>
      )}

      {summary && summary.assetsWithMissingPriceData > 0 && (
        <div className="text-muted text-center mb-3 d-flex align-items-center justify-content-center gap-2" style={{ fontSize: '0.8rem' }}>
          <AlertTriangle size={14} className="text-warning" />
          {t('reporting.maintenance_spend_by_asset.missing_price_summary', '{{count}} asset has parts with missing unit price — totals may be understated.', { count: summary.assetsWithMissingPriceData })}
        </div>
      )}

      {/* Card list */}
      <div>
        {items.map((asset) => (
          <AssetSpendCard
            key={asset.assetId}
            asset={asset}
            currency={currency}
            maxSpend={maxSpend}
          />
        ))}
      </div>

      {/* "See all" */}
      {pagination && pagination.total > items.length && (
        <div className="text-center mt-3">
          <button
            type="button"
            className="btn btn-link p-0"
            onClick={() => navigate('/reporting/asset-spend')}
            style={{ fontSize: '0.85rem' }}
          >
            {t('reporting.maintenance_spend_by_asset.see_all', 'See all {{count}} assets', { count: pagination.total })}
            <ChevronRight size={14} className="ms-1" />
          </button>
        </div>
      )}
    </ReportWidget>
  );
};

export default MaintenanceSpendByAssetWidget;
